import { defineAsyncComponent } from "vue";
import {
    createColumnAttachment,
    createColumnInlineCheckbox,
    createColumnInput,
    createColumnSelect
} from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import { createButtonCreate } from "../../../../utils/button";
import { conditionFormChecker, onClickDeleteAllClient } from "../../../../utils/form";
import { onIFrameClickSelectAll } from "../../../../utils/iframe";
import { createLoading } from "../../../../utils/loading";
import { has } from "../../../../utils/menu-context";
import { createManageDelete, createManageExportCsv, createPage } from "../../../../utils/page";
import { openModalList as openModalListCountry } from "../../master-mgt/country-request/country-request.logic";
import { Form, Tag } from "ant-design-vue";
import lodash from "lodash";

const Attachment = defineAsyncComponent(() => import("../../../../components/input/template-builder/attachment/Attachment"));
const CSelect = defineAsyncComponent(() => import("../../../../components/input/template-builder/select/CSelect"));

export const { isLoading, setIsLoading } = createLoading();

export const callCompanyCountryStatus = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_COUNTRY_STATUS", value: searchQuery });
export const callCompanyCountryStatusByKey = ({ key }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "COMPANY_COUNTRY_STATUS", key });

export const createColumns = ({ props, data }) => {
    const { viewOnly, disabledCheckReject, disabledStatus, itemsKey, visibleCheckReject, visibleCompany, visibleCountry } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    const columns = [];
    if (visibleCheckReject) {
        columns.push(
            createColumnInlineCheckbox({
                title: () => i18n.global.t("model.companyCountry.isCheckedReject"),
                dataIndex: "isCheckedReject",
                customRenderInputProp: ({ record }) => ({ disabled: disabledCheckReject || ["Cancel"].includes(record.refStatus?.key) })
            })
        );
    }
    if (visibleCompany) {
        columns.push(createColumnInput({ title: () => i18n.global.t("model.company.thNo"), dataIndex: "refCompany.thNo", align: "center" }));
        columns.push(createColumnInput({ title: () => i18n.global.t("model.company.nameTh"), dataIndex: "refCompany.nameTh", width: 200 }));
        columns.push(createColumnInput({ title: () => i18n.global.t("model.company.nameEn"), dataIndex: "refCompany.nameEn", width: 200 }));
    }
    if (visibleCountry) {
        columns.push(createColumnInput({ 
            title: () => i18n.global.t("model.country.nameTh"),
            dataIndex: "refCountry.nameTh",
            align: "center",
            customRender: ({ record }) => (
                <div>
                    <div>{record.refCountry?.nameTh}</div>
                    {!record.refCountry?.isSpecial ? (
                        <Tag color="warning" class="px-1 mx-0 ml-2">
                            <small>{i18n.global.t("views.company.companyRequest.type1.companyCountry.isNotSpecial")}</small>
                        </Tag>
                    ) : null}
                </div>
            )
        }));
        columns.push(createColumnInput({ title: () => i18n.global.t("model.country.nameEn"), dataIndex: "refCountry.nameEn", align: "center" }));
        columns.push(createColumnAttachment({ title: () => i18n.global.t("model.country.refAttach"), dataIndex: "refCountry.refAttach" }));
    }
    columns.push(
        createColumnSelect({
            title: () => i18n.global.t("model.companyCountry.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callCompanyCountryStatus, optLabel: "value" }),
            customRender: ({ record }) => {
                const index = items.findIndex(d => d === record);
                const key = `${itemsKey}.${index}.refStatus`;
                const inputProp = lodash.merge(
                    {
                        value: record.refStatus,
                        "onUpdate:value": value => {
                            record.isSave = true;
                            record.refStatus = value;
                        },
                        list: callCompanyCountryStatus,
                        optLabel: "value",
                        disabled: disabledStatus,
                        allowClear: false
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                );
                return (
                    <Form.Item {...useForm.validateInfos[key]} wrapperCol={{ xs: 24 }}>
                        <CSelect {...inputProp}></CSelect>
                    </Form.Item>
                );
            },
            search: { name: "refStatus.id" },
            width: 210
        })
    );
    columns.push({
        title: () => i18n.global.t("model.companyCountry.refAttach"),
        sorter: false,
        width: 150,
        align: "center",
        visible: () => true,
        search: { visible: () => false },
        customRender: ({ record }) => {
            if (!record.refCountry?.refAttach) {
                return null;
            }
            const index = items.findIndex(d => d === record);
            return (
                <Form.Item {...useForm.validateInfos[`${itemsKey}.${index}.refAttach`]}>
                    <Attachment value={record.refAttach} onUpdate:value={value => (record.refAttach = value)} disabled={viewOnly}></Attachment>
                </Form.Item>
            );
        }
    });
    return columns;
};

export const initData = ({ props, data }) => {
    const { menuContext, viewOnly, itemsKey, visibleCompany, visibleCountry, selectGetCheckboxPropDisabled } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    data.columns = createColumns({ props, data });
    data.page = createPage({ data: items });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => (viewOnly ? false : has({ menuContext, key: "canDelete" }) || props.canDelete),
            onClick: ({ rowSelection }) => onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey })
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [];
    data.rowActions = [];

    if (!viewOnly) {
        if (visibleCompany) {
            /*
            data.manageExtras.push({
                render: () => ({
                    ...createButtonCreate({
                        onClick: () => openModalListCompany({
                            onIframeMounted: ({ refs, options }) => onIFrameClickSelectAll({
                                refs, options, items, itemKey: 'refCompany.id', rowKey: 'id', setKey: 'refCompany'
                            }),
                        }),
                        options: { text: i18n.global.t('model.companyCountry.refCompany') }
                    }),
                }),
                visible: () => has({ menuContext, key: 'canCreate' }),
            })
            */
        }
        if (visibleCountry) {
            data.manageExtras.push({
                render: () => ({
                    ...createButtonCreate({
                        onClick: () =>
                            openModalListCountry({
                                onIframeMounted: ({ refs, options }) => {
                                    onIFrameClickSelectAll({
                                        refs,
                                        options,
                                        items,
                                        itemKey: "refCountry.id",
                                        rowKey: "id",
                                        setKey: "refCountry",
                                        afterSelect: () => {
                                            callCompanyCountryStatusByKey({ key: "Draft" }).then(statusDraft => {
                                                items.forEach(item => {
                                                    if (!item.refStatus) item.refStatus = statusDraft[0];
                                                });
                                            });
                                        }
                                    });
                                    refs.iframe.contentWindow.selectGetCheckboxPropDisabled = selectGetCheckboxPropDisabled;
                                }
                            }),
                        options: { text: i18n.global.t("model.companyCountry.refCountry") }
                    })
                }),
                visible: () => has({ menuContext, key: "canCreate" })
            });
        }
    }
};
